import { FIELD_TYPES } from "features/MediationForm/validationSchema";

export const MULTI_SELECT = "multi_select";

// ─────────────────────────────────────────────────────────────────────────────
// ACCB Arbitration Form Schema
//
// Sections rendered in order:
//   0. Case Category          — CMP / Reference / Institutional / Other + conditional ref number
//   1. Claimant Details       — multi-party (repeatable)
//   2. Claimant Counsel       — multi-party (repeatable)
//   3. Respondent Details     — multi-party (repeatable)
//   4. Respondent Counsel     — multi-party (repeatable)
//   5. Document Upload        — label driven by case category
//   6. Arbitration Details      — dispute summary, language, timeline
//
// Multi-party sections carry  multiParty: true  so ArbitrationRequestPage knows
// to render them as repeatable blocks instead of a static FormSection.
// ─────────────────────────────────────────────────────────────────────────────

// ── Case categories ────────────────────────────────────────────────────────
export const CASE_CATEGORIES = {
  CMP: "cmp",
  REFERENCE: "reference",
  INSTITUTIONAL: "institutional",
  OTHER: "other",
};

// ── 0. Case Category ──────────────────────────────────────────────────────
export const caseCategorySection = {
  sectionId: "case_category",
  sectionTitle: "Case Category",
  sectionSubtitle: "Select the type of case you are filing for ",
  fields: [
    {
      id: "case_category",
      label: "Case Category",
      type: FIELD_TYPES.SELECT,
      placeholder: "Select a category",
      required: true,
      options: [
        { value: CASE_CATEGORIES.CMP, label: "CMP Case" },
        { value: CASE_CATEGORIES.REFERENCE, label: "Reference Case" },
        { value: CASE_CATEGORIES.INSTITUTIONAL, label: "Institutional Case" },
        { value: CASE_CATEGORIES.OTHER, label: "Other" },
      ],
      gridSize: { xs: 12, sm: 6 },
    },
    {
      id: "cmp_number",
      label: "CMP Number",
      type: FIELD_TYPES.TEXT,
      placeholder: "Enter CMP number",
      required: true,
      // shown only when category === "cmp"
      dependsOn: { field: "case_category", value: CASE_CATEGORIES.CMP },
      gridSize: { xs: 12, sm: 6 },
    },
    {
      id: "msed_number",
      label: "MSEFC Number",
      type: FIELD_TYPES.TEXT,
      placeholder: "Enter MSEFC number",
      required: true,
      // shown only when category === "reference"
      dependsOn: { field: "case_category", value: CASE_CATEGORIES.REFERENCE },
      gridSize: { xs: 12, sm: 6 },
    },
    {
      id: "region",
      label: "City / District",
      type: FIELD_TYPES.SELECT,
      placeholder: "Select city / district",
      // shown only when category === "reference"
      dependsOn: { field: "case_category", value: CASE_CATEGORIES.REFERENCE },
      options: [],
      gridSize: { xs: 12, sm: 6 },
    },
  ],
};

// ── 1. Claimant Details (per-party template) ───────────────────────────────
// Used by ArbitrationRequestPage to generate fields for each claimant entry.
// `countryOptions` is supplied at render-time from the /api/country/ list.
export const claimantFieldTemplate = (index, countryOptions = []) => [
  {
    id: `claimants[${index}].name`,
    label: "Claimant Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter full legal name",
    required: true,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].is_claimant_organisation`,
    label: "Is Claimant an Organisation?",
    type: FIELD_TYPES.SELECT,
    placeholder: "Select",
    required: true,
    options: [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ],
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].representative_name`,
    label: "Representative Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter representative name (optional)",
    required: false,
    dependsOn: { field: `claimants[${index}].is_claimant_organisation`, value: "true" },
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].email`,
    label: "Email ID",
    type: FIELD_TYPES.EMAIL,
    placeholder: "example@domain.com",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].mobile`,
    label: "Phone Number",
    type: FIELD_TYPES.TEL,
    placeholder: "Enter phone number",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].address`,
    label: "Address (1st line)",
    type: FIELD_TYPES.TEXT,
    placeholder: "House / Flat / Building, Street",
    required: false,
    gridSize: { xs: 12 },
  },
  {
    id: `claimants[${index}].country`,
    label: "Country",
    type: FIELD_TYPES.SELECT,
    required: true,
    options: countryOptions,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].state_code`,
    label: "State",
    type: FIELD_TYPES.SELECT,
    required: false,
    options: [],
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].city`,
    label: "City",
    type: FIELD_TYPES.SELECT,
    required: false,
    options: [],
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimants[${index}].pincode`,
    label: "Pin Code",
    type: FIELD_TYPES.TEXT,
    placeholder: "6-digit PIN",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
];

// ── 2. Claimant Counsel (per-party template) ───────────────────────────────
export const claimantCounselFieldTemplate = (index, partyOptions = []) => [
  {
    id: `claimant_counsels[${index}].name`,
    label: "Claimant Counsel Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter counsel name",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimant_counsels[${index}].law_firm_name`,
    label: "Law Firm Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter law firm name",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimant_counsels[${index}].email`,
    label: "Email ID",
    type: FIELD_TYPES.EMAIL,
    placeholder: "counsel@firm.com",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimant_counsels[${index}].phone`,
    label: "Phone Number",
    type: FIELD_TYPES.TEL,
    placeholder: "Enter phone number",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimant_counsels[${index}].bar_enrollment`,
    label: "Bar Enrollment No.",
    type: FIELD_TYPES.TEXT,
    placeholder: "e.g. MH/1234/2010",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `claimant_counsels[${index}].claimant_ids`,
    label: "Represents Claimant(s)",
    type: MULTI_SELECT,
    placeholder: "Select one or more claimants",
    helperText: "A counsel may represent more than one claimant.",
    required: false,
    options: partyOptions,
    gridSize: { xs: 12 },
  },
];

// ── 3. Respondent Details (per-party template) ────────────────────────────
// `countryOptions` is supplied at render-time from the /api/country/ list.
export const respondentFieldTemplate = (index, countryOptions = []) => [
  {
    id: `respondents[${index}].name`,
    label: "Respondent Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter full legal name",
    required: true,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].is_respondent_organisation`,
    label: "Is Respondent an Organisation?",
    type: FIELD_TYPES.SELECT,
    placeholder: "Select",
    required: true,
    options: [
      { value: "true", label: "Yes" },
      { value: "false", label: "No" },
    ],
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].representative_name`,
    label: "Representative Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter representative name (optional)",
    required: false,
    dependsOn: { field: `respondents[${index}].is_respondent_organisation`, value: "true" },
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].email`,
    label: "Email ID",
    type: FIELD_TYPES.EMAIL,
    placeholder: "example@domain.com",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].mobile`,
    label: "Phone Number",
    type: FIELD_TYPES.TEL,
    placeholder: "Enter phone number",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].address`,
    label: "Address (1st line)",
    type: FIELD_TYPES.TEXT,
    placeholder: "House / Flat / Building, Street",
    required: false,
    gridSize: { xs: 12 },
  },
  {
    id: `respondents[${index}].country`,
    label: "Country",
    type: FIELD_TYPES.SELECT,
    required: true,
    options: countryOptions,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].state_code`,
    label: "State",
    type: FIELD_TYPES.SELECT,
    required: false,
    options: [],
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].city`,
    label: "City",
    type: FIELD_TYPES.SELECT,
    required: false,
    options: [],
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondents[${index}].pincode`,
    label: "Pin Code",
    type: FIELD_TYPES.TEXT,
    placeholder: "6-digit PIN",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
];

// ── 4. Respondent Counsel (per-party template) ────────────────────────────// `partyOptions` is supplied at render-time from the live respondent list.
export const respondentCounselFieldTemplate = (index, partyOptions = []) => [
  {
    id: `respondent_counsels[${index}].name`,
    label: "Respondent Counsel Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter counsel name",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondent_counsels[${index}].law_firm_name`,
    label: "Law Firm Name",
    type: FIELD_TYPES.TEXT,
    placeholder: "Enter law firm name",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondent_counsels[${index}].email`,
    label: "Email ID",
    type: FIELD_TYPES.EMAIL,
    placeholder: "counsel@firm.com",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondent_counsels[${index}].phone`,
    label: "Phone Number",
    type: FIELD_TYPES.TEL,
    placeholder: "Enter phone number",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondent_counsels[${index}].bar_enrollment`,
    label: "Bar Enrollment No.",
    type: FIELD_TYPES.TEXT,
    placeholder: "e.g. MH/1234/2010",
    required: false,
    gridSize: { xs: 12, sm: 6 },
  },
  {
    id: `respondent_counsels[${index}].respondent_ids`,
    label: "Represents Respondent(s)",
    type: MULTI_SELECT,
    placeholder: "Select one or more respondents",
    helperText: "A counsel may represent more than one respondent.",
    required: false,
    options: partyOptions,
    gridSize: { xs: 12 },
  },
];

// ── 5. Document Upload ─────────────────────────────────────────────────────
// label / helperText is derived at render-time from case_category value.
export const documentUploadField = {
  id: "documents",
  label: "Upload Documents",           // overridden dynamically in ArbitrationRequestPage
  type: FIELD_TYPES.FILE_UPLOAD,
  accept: ".pdf,.doc,.docx,.jpg,.jpeg,.png",
  multiple: true,
  required: false,
  helperText: "Accepted formats: PDF, DOC, DOCX, JPG, PNG (max 10 MB each)",
  gridSize: { xs: 12 },
};

// ── Helpers used by ArbitrationRequestPage ──────────────────────────────────

/** Returns upload field label/helperText based on selected category. */
export const getDocumentUploadMeta = (category) => {
  switch (category) {
    case CASE_CATEGORIES.CMP:
      return {
        label: "Upload Court Order",
        helperText: "Please upload the Court order for this CMP case.",
      };
    case CASE_CATEGORIES.REFERENCE:
      return {
        label: "Upload MSEFC Reference Order",
        helperText: "Please upload the MSEFC reference order.",
      };
    default:
      return {
        label: "Upload Supporting Documents",
        helperText: "Kindly upload any supporting documents related to the dispute.",
      };
  }
};

let claimantSeq = 0;
let respondentSeq = 0;

/** Blank party objects — used to initialise state arrays. */
export const blankClaimant = () => ({
  id: `c${++claimantSeq}`,
  name: "", is_claimant_organisation: "", representative_name: "",
  email: "", mobile: "", address: "", city: "", state: "", state_code: "", pincode: "", country: "",
});

export const blankClaimantCounsel = () => ({
  name: "", law_firm_name: "", email: "", phone: "", bar_enrollment: "", claimant_ids: [],
});

export const blankRespondent = () => ({
  id: `r${++respondentSeq}`,
  name: "", is_respondent_organisation: "", representative_name: "",
  email: "", mobile: "", address: "", city: "", state: "", state_code: "", pincode: "", country: "",
});

export const blankRespondentCounsel = () => ({
  name: "", law_firm_name: "", email: "", phone: "", bar_enrollment: "", respondent_ids: [],
});

export const buildPartyOptions = (parties, label) =>
  parties.map((party, idx) => ({
    value: party.id,
    label: party.name && party.name.trim()
      ? `${label} ${idx + 1} — ${party.name.trim()}`
      : `${label} ${idx + 1}`,
  }));
